import { DownOutlineIcon } from '@carboncopyco/icons';
import {
  Children,
  type ReactElement,
  cloneElement,
  useRef,
  useState,
} from 'react';
import { Button, type ButtonProps } from '../Button';
import { Dropdown, type DropdownProps } from '../Dropdown';
import { TooltipBubble } from '../TooltipBubble';

export interface SplitButtonDropdownProps
  extends Pick<ButtonProps, 'disabled' | 'secondary' | 'leftIcon' | 'label'> {
  onClick: () => void;
  children: ReactElement | ReactElement[];
  dropdownProps?: Omit<
    DropdownProps,
    'children' | 'open' | 'asChild' | 'onInteractOutside' | 'triggerElement'
  >;
  primaryButtonTestId?: string;
  dropdownTriggerButtonTestId?: string;
  primaryTooltipTitle?: string;
  dropdownTriggerTooltipTitle?: string;
}

export const SplitButtonDropdown = ({
  disabled,
  secondary,
  leftIcon,
  label,
  onClick,
  children,
  primaryButtonTestId,
  dropdownTriggerButtonTestId,
  primaryTooltipTitle,
  dropdownTriggerTooltipTitle,
  dropdownProps,
}: SplitButtonDropdownProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const primaryButtonRef = useRef(null);
  const triggerButtonRef = useRef(null);
  const triggerButtonBorderClasses = secondary
    ? '[&>button:last-child]:!border-s-0'
    : '[&>button:last-child]:!border-s-px [&>button:last-child]:!border-s-solid [&>button:last-child]:!border-s-[rgba(255,255,255,0.5)]';

  const handleChildClick = (
    event: MouseEvent,
    onChildClick?: (event: MouseEvent) => void
  ) => {
    setIsDropdownOpen(false);

    if (onChildClick) {
      onChildClick(event);
    }
  };

  return (
    <div className={`flex ${triggerButtonBorderClasses}`}>
      <Button
        label={label}
        disabled={disabled}
        secondary={secondary}
        leftIcon={leftIcon}
        data-testid={primaryButtonTestId}
        onClick={onClick}
        ref={primaryButtonRef}
        straightCorners={['tr', 'br']}
      />
      {primaryTooltipTitle && (
        <TooltipBubble
          title={primaryTooltipTitle}
          placement="top"
          anchorRef={primaryButtonRef}
        />
      )}

      <Dropdown
        width={dropdownProps?.width}
        align={dropdownProps?.align}
        open={isDropdownOpen}
        asChild
        onInteractOutside={() => {
          setIsDropdownOpen(false);
        }}
        side={dropdownProps?.side ?? 'bottom'}
        triggerElement={
          <Button
            xSmall
            icon={<DownOutlineIcon size={16} />}
            onClick={() => {
              setIsDropdownOpen(!isDropdownOpen);
            }}
            disabled={disabled}
            secondary={secondary}
            data-testid={dropdownTriggerButtonTestId}
            ref={triggerButtonRef}
            straightCorners={['tl', 'bl']}
          />
        }
        {...(dropdownProps ?? {})}
      >
        {Children.map(children, (child: ReactElement) =>
          cloneElement(child, {
            onClick: (event: MouseEvent) =>
              handleChildClick(event, child.props.onClick),
          })
        )}
      </Dropdown>

      {dropdownTriggerTooltipTitle && (
        <TooltipBubble
          title={dropdownTriggerTooltipTitle}
          placement="top"
          anchorRef={triggerButtonRef}
        />
      )}
    </div>
  );
};
